import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BarChart3, AlertTriangle, CheckCircle, XCircle, Filter, Calendar, Database, Search, RefreshCw, Gift, Download, CalendarOff, Shield, TrendingUp, ChevronDown, ChevronUp, ArrowLeft, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { getAllowedMarkets, isSymbolAllowed } from '../services/api';

// Helper function to format dates to DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';

  // If already in DD-MM-YYYY format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  try {
    // Parse the date string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // Return original if invalid

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return dateStr; // Return original if parsing fails
  }
};

const Indexes = () => {
  const [data, setData] = useState(null);
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Refs for request cancellation and debouncing
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Get allowed markets for current user
  const allowedMarkets = getAllowedMarkets();

  // Get initial filters from URL params
  const urlParams = new URLSearchParams(location.search);
  const urlMarket = urlParams.get('market');
  // Validate URL market against allowed markets, fallback to first allowed market
  const defaultMarket = allowedMarkets.includes('all') ? 'india' : (allowedMarkets[0] || 'india');
  const initialMarket = urlMarket && (allowedMarkets.includes('all') || allowedMarkets.includes(urlMarket))
    ? urlMarket
    : defaultMarket;
  const initialErrors = urlParams.get('errors') || 'errors_only';
  const initialSymbol = urlParams.get('symbol') || '';

  const [filters, setFilters] = useState({
    market: initialMarket,
    symbol: initialSymbol,
    errors: initialErrors,
    days: 90,
    dateRangeType: 'days', // 'days' or 'custom'
    startDate: '',
    endDate: ''
  });

  // Bias analysis collapsible state
  const [biasAnalysisExpanded, setBiasAnalysisExpanded] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState('quality_monitor');

  // Non-Negotiables state
  const [nonNegotiablesHtml, setNonNegotiablesHtml] = useState(null);
  const [nonNegotiablesLoading, setNonNegotiablesLoading] = useState(false);
  const [nonNegotiablesError, setNonNegotiablesError] = useState(null);

  // TA indicators visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    close: true,
    high: true,
    low: true,
    daily_psar: true,
    daily_psar_tsl_level: true,
    daily_psar_deviation: true,
    drsi: true,
    wrsi: true,
    sma_50d: true,
    dema7: true,
    dema10: true,
    dema20: true,
    weekly_macd_hist: true,
    daily_macd_hist: true,
    wsma20: true,
    wema21: true,
    sma_200d: true,
    sma_200w: true,
    weekly_bb_upper: true,
    weekly_bb_mid: true,
    weekly_bb_lower: true,
    daily_bb_upper: false,
    daily_bb_mid: false,
    daily_bb_lower: false,
    weekly_volatility: true,
    smart_index_st: true,
    // Derived indicator columns for smart_index validation
    slope_sma50d_over_10d: true,
    slope_sma100d_over_60d: true,
    slope_dema20_over_30d: true,
    slope_dema20_over_5d: true,
    gap_pct_close_vs_sma50d: true,
    gap_pct_sma50d_vs_sma100d: true,
    wmacd_wave_type: true,
    wmacd_wave_worst: true,
    wmacd_wave_best: true
  });

  // Focus columns state - columns to highlight
  const [focusColumns, setFocusColumns] = useState({
    close: false,
    high: false,
    low: false,
    daily_psar: false,
    daily_psar_tsl_level: false,
    daily_psar_deviation: false,
    drsi: false,
    wrsi: false,
    sma_50d: false,
    dema7: false,
    dema10: false,
    dema20: false,
    weekly_macd_hist: false,
    daily_macd_hist: false,
    wsma20: false,
    wema21: false,
    sma_200d: false,
    sma_200w: false,
    weekly_bb_upper: false,
    weekly_bb_mid: false,
    weekly_bb_lower: false,
    daily_bb_upper: false,
    daily_bb_mid: false,
    daily_bb_lower: false,
    weekly_volatility: false,
    smart_index_st: false,
    // Derived indicator columns
    slope_sma50d_over_10d: false,
    slope_sma100d_over_60d: false,
    slope_dema20_over_30d: false,
    slope_dema20_over_5d: false,
    gap_pct_close_vs_sma50d: false,
    gap_pct_sma50d_vs_sma100d: false,
    wmacd_wave_type: false,
    wmacd_wave_worst: false,
    wmacd_wave_best: false
  });

  // Column definitions with metadata
  const columnDefinitions = useMemo(() => [
    { key: 'close', label: 'Close', decimals: 2 },
    { key: 'high', label: 'High', decimals: 2 },
    { key: 'low', label: 'Low', decimals: 2 },
    { key: 'daily_psar', label: 'PSAR Daily', decimals: 2 },
    { key: 'daily_psar_tsl_level', label: 'PSAR TSL', decimals: 2 },
    { key: 'daily_psar_deviation', label: 'PSAR Dev %', decimals: 2 },
    { key: 'drsi', label: 'DRSI', decimals: 0 },
    { key: 'wrsi', label: 'WRSI', decimals: 0 },
    { key: 'sma_50d', label: 'SMA 50D', decimals: 2, hasBiasCheck: true },
    { key: 'dema7', label: 'DEMA 7', decimals: 2 },
    { key: 'dema10', label: 'DEMA 10', decimals: 2 },
    { key: 'dema20', label: 'DEMA 20', decimals: 2 },
    { key: 'weekly_macd_hist', label: 'Weekly MACD', decimals: 4, hasBiasCheck: true },
    { key: 'daily_macd_hist', label: 'Daily MACD', decimals: 4 },
    { key: 'wsma20', label: 'WSMA20', decimals: 2 },
    { key: 'wema21', label: 'WEMA21', decimals: 2 },
    { key: 'sma_200d', label: 'SMA 200D', decimals: 2, hasBiasCheck: true, stocksOnly: true },
    { key: 'sma_200w', label: 'SMA 200W', decimals: 2, hasBiasCheck: true },
    { key: 'weekly_bb_upper', label: 'BB Upper (W)', decimals: 2 },
    { key: 'weekly_bb_mid', label: 'BB Mid (W)', decimals: 2 },
    { key: 'weekly_bb_lower', label: 'BB Lower (W)', decimals: 2, hasBiasCheck: true },
    { key: 'daily_bb_upper', label: 'BB Upper (D)', decimals: 2 },
    { key: 'daily_bb_mid', label: 'BB Mid (D)', decimals: 2 },
    { key: 'daily_bb_lower', label: 'BB Lower (D)', decimals: 2 },
    { key: 'weekly_volatility', label: 'Volatility', decimals: 4 },
    // Derived indicator columns for smart_index validation
    { key: 'slope_sma50d_over_10d', label: 'Slope SMA50/10d', decimals: 4, isRequired: true },
    { key: 'slope_sma100d_over_60d', label: 'Slope SMA100/60d', decimals: 4, isRequired: true },
    { key: 'slope_dema20_over_30d', label: 'Slope DEMA20/30d', decimals: 4, isRequired: true },
    { key: 'slope_dema20_over_5d', label: 'Slope DEMA20/5d', decimals: 4, isRequired: true },
    { key: 'gap_pct_close_vs_sma50d', label: 'Gap% Close/SMA50', decimals: 2, isRequired: true },
    { key: 'gap_pct_sma50d_vs_sma100d', label: 'Gap% SMA50/SMA100', decimals: 2, isRequired: true },
    { key: 'wmacd_wave_type', label: 'W-MACD Wave', decimals: 0, isText: true, isRequired: true },
    { key: 'wmacd_wave_worst', label: 'W-MACD Worst', decimals: 4, isRequired: true },
    { key: 'wmacd_wave_best', label: 'W-MACD Best', decimals: 4, isRequired: true }
  ], []);

  // Sort columns: focused ones first, then non-focused
  const sortedColumns = useMemo(() => {
    return [...columnDefinitions].sort((a, b) => {
      const aFocused = focusColumns[a.key];
      const bFocused = focusColumns[b.key];
      if (aFocused && !bFocused) return -1;
      if (!aFocused && bFocused) return 1;
      return 0;
    });
  }, [columnDefinitions, focusColumns]);

  // All market options (allowedMarkets is defined at top of component)
  // Note: precious_metals and crypto are hidden for now
  const allMarketOptions = [
    { value: 'india', label: 'India', icon: '🇮🇳' },
    { value: 'us', label: 'US', icon: '🇺🇸' },
    { value: 'asia_pacific', label: 'Asia Pacific', icon: '🌏' },
    { value: 'europe', label: 'Europe', icon: '🇪🇺' },
  ];

  // Filter market options based on user's allowed markets
  const marketOptions = allowedMarkets.includes('all')
    ? allMarketOptions
    : allMarketOptions.filter(m => allowedMarkets.includes(m.value));

  const errorFilterOptions = [
    { value: 'errors_only', label: 'Issues Only' },
    { value: 'all', label: 'All Data (including holidays)' }
  ];

  const daysOptions = [
    { value: 30, label: 'Last 30 Days' },
    { value: 60, label: 'Last 60 Days' },
    { value: 90, label: 'Last 90 Days' },
    { value: 180, label: 'Last 180 Days' },
    { value: 365, label: 'Last 365 Days' }
  ];

  // Load available symbols when market changes
  useEffect(() => {
    fetchAvailableSymbols();
  }, [filters.market]);

  // Fetch data when filters change with debouncing
  useEffect(() => {
    if (!filters.symbol) return;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Set new debounce timer (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      fetchData();
    }, 300);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const fetchAvailableSymbols = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/analytics/indexes/?market=${filters.market}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.available_symbols) {
        // Filter symbols based on user's allowed symbols for this market
        const filteredSymbols = data.available_symbols.filter(symbol =>
          isSymbolAllowed(filters.market, symbol.symbol_code)
        );
        setAvailableSymbols(filteredSymbols);
        // Auto-select first symbol if none selected
        if (!filters.symbol && filteredSymbols.length > 0) {
          setFilters(prev => ({ ...prev, symbol: filteredSymbols[0].symbol_code }));
        }
      }
    } catch (error) {
      console.error('Error fetching symbols:', error);
      setError('Failed to load available symbols');
    }
  };

  const fetchData = async () => {
    if (!filters.symbol) return;

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      queryParams.append('market', filters.market);
      queryParams.append('symbol', filters.symbol);
      queryParams.append('errors', filters.errors);

      // Use custom date range if selected, otherwise use days
      if (filters.dateRangeType === 'custom' && filters.startDate && filters.endDate) {
        queryParams.append('start_date', filters.startDate);
        queryParams.append('end_date', filters.endDate);
      } else {
        queryParams.append('days', filters.days);
      }

      const response = await fetch(`${API_BASE_URL}/analytics/indexes/?${queryParams}`, {
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData);

    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Non-Negotiables report for current symbol
  const fetchNonNegotiables = async () => {
    if (!filters.symbol) return;

    setNonNegotiablesLoading(true);
    setNonNegotiablesError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/analytics/reports/non-negotiables/${filters.symbol}/`);
      if (!response.ok) {
        if (response.status === 404) {
          setNonNegotiablesHtml(null);
          setNonNegotiablesError('No validation report available for this symbol');
        } else {
          throw new Error(`Failed to fetch report`);
        }
        return;
      }
      const data = await response.json();
      setNonNegotiablesHtml(data.html_content);
    } catch (err) {
      console.error('Error fetching non-negotiables:', err);
      setNonNegotiablesError(err.message);
    } finally {
      setNonNegotiablesLoading(false);
    }
  };

  // Fetch Non-Negotiables when tab changes to it or symbol changes while on that tab
  useEffect(() => {
    if (activeTab === 'non_negotiables' && filters.symbol) {
      fetchNonNegotiables();
    }
  }, [activeTab, filters.symbol]);

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'critical':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'missing':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'weekend':
        return <CalendarOff className="w-4 h-4 text-gray-400" />;
      case 'holiday':
        return <Gift className="w-4 h-4 text-blue-400" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getHealthBadge = (status) => {
    const badges = {
      healthy: 'px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20',
      warning: 'px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full border border-yellow-500/20',
      critical: 'px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20',
      missing: 'px-2 py-1 bg-red-600/10 text-red-600 text-xs rounded-full border border-red-600/20',
      weekend: 'px-2 py-1 bg-gray-500/10 text-gray-400 text-xs rounded-full border border-gray-500/20',
      holiday: 'px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full border border-blue-500/20'
    };

    return (
      <span className={badges[status] || badges.warning}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    return score;
  };

  const exportToCSV = () => {
    if (!data || !data.records || data.records.length === 0) {
      alert('No data to export');
      return;
    }

    // Define all columns we want to export
    const columns = [
      { key: 'date', label: 'Date' },
      { key: 'health_status', label: 'Health Status' },
      { key: 'close', label: 'Close' },
      { key: 'high', label: 'High' },
      { key: 'low', label: 'Low' },
      { key: 'daily_psar', label: 'PSAR Daily' },
      { key: 'daily_psar_tsl_level', label: 'PSAR TSL' },
      { key: 'daily_psar_deviation', label: 'PSAR Dev %' },
      { key: 'drsi', label: 'DRSI' },
      { key: 'wrsi', label: 'WRSI' },
      { key: 'sma_50d', label: 'SMA 50D' },
      { key: 'dema7', label: 'DEMA 7' },
      { key: 'dema10', label: 'DEMA 10' },
      { key: 'dema20', label: 'DEMA 20' },
      { key: 'weekly_macd_hist', label: 'Weekly MACD Hist' },
      { key: 'daily_macd_hist', label: 'Daily MACD Hist' },
      { key: 'wsma20', label: 'WSMA20' },
      { key: 'wema21', label: 'WEMA21' },
      { key: 'sma_200d', label: 'SMA 200D' },
      { key: 'sma_200w', label: 'SMA 200W' },
      { key: 'weekly_bb_upper', label: 'BB Upper (W)' },
      { key: 'weekly_bb_mid', label: 'BB Mid (W)' },
      { key: 'weekly_bb_lower', label: 'BB Lower (W)' },
      { key: 'daily_bb_upper', label: 'BB Upper (D)' },
      { key: 'daily_bb_mid', label: 'BB Mid (D)' },
      { key: 'daily_bb_lower', label: 'BB Lower (D)' },
      { key: 'weekly_volatility', label: 'Volatility' },
      { key: 'smart_index_st', label: 'Smart Index ST' },
      // Derived indicator columns
      { key: 'slope_sma50d_over_10d', label: 'Slope SMA50/10d' },
      { key: 'slope_sma100d_over_60d', label: 'Slope SMA100/60d' },
      { key: 'slope_dema20_over_30d', label: 'Slope DEMA20/30d' },
      { key: 'slope_dema20_over_5d', label: 'Slope DEMA20/5d' },
      { key: 'gap_pct_close_vs_sma50d', label: 'Gap% Close/SMA50' },
      { key: 'gap_pct_sma50d_vs_sma100d', label: 'Gap% SMA50/SMA100' },
      { key: 'wmacd_wave_type', label: 'W-MACD Wave' },
      { key: 'wmacd_wave_worst', label: 'W-MACD Worst' },
      { key: 'wmacd_wave_best', label: 'W-MACD Best' }
    ];

    // Create CSV header
    const header = columns.map(col => col.label).join(',');

    // Create CSV rows
    const rows = data.records.map(record => {
      return columns.map(col => {
        const value = record[col.key];

        // Handle null/undefined values
        if (value === null || value === undefined) {
          return 'NULL';
        }

        // Handle boolean values
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }

        // Format numbers with appropriate precision
        if (typeof value === 'number') {
          // Slope and MACD columns need 4 decimals
          if (col.key.includes('macd') || col.key === 'weekly_volatility' || col.key.includes('slope_')) {
            return value.toFixed(4);
          } else if (col.key === 'drsi' || col.key === 'wrsi' || col.key === 'smart_index_st') {
            return Math.round(value);
          } else if (col.key.includes('gap_pct_')) {
            return value.toFixed(2);
          } else if (col.key.startsWith('is_')) {
            // Boolean stored as 0/1
            return value === 1 ? 'Yes' : 'No';
          } else {
            return value.toFixed(2);
          }
        }

        // Handle strings (escape commas and quotes)
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    // Combine header and rows
    const csv = [header, ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with current filters
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${filters.market}_${filters.symbol}_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };

      // Reset symbol if market changes
      if (key === 'market') {
        newFilters.symbol = '';
        setData(null);
      }

      return newFilters;
    });
  };

  // Helper to render table header for a column
  const renderColumnHeader = (column) => {
    if (!visibleColumns[column.key]) return null;

    return (
      <th
        key={column.key}
        className={`sticky top-0 z-50 text-left py-4 px-4 text-dark-100 font-semibold shadow-[0_2px_4px_rgba(0,0,0,0.4)] ${
          focusColumns[column.key] ? 'bg-indigo-700/60' : 'bg-dark-700'
        }`}
      >
        {column.label}
      </th>
    );
  };

  // Helper to render table cell for a column
  const renderColumnCell = (column, record, biasIssues, isNonTrading, isCrypto, isStocks) => {
    if (!visibleColumns[column.key]) return null;

    // Check for bias issues on this specific column
    let hasBiasIssue = false;
    if (column.hasBiasCheck) {
      if (column.key === 'weekly_macd_hist') {
        hasBiasIssue = biasIssues.some(i => i.type === 'weekly_macd_static');
      } else if (column.key === 'weekly_bb_lower') {
        hasBiasIssue = biasIssues.some(i => i.type === 'bb_static');
      } else if (column.key === 'sma_50d') {
        hasBiasIssue = biasIssues.some(i => i.type === 'sma_50d_static');
      } else if (column.key === 'sma_200d') {
        hasBiasIssue = biasIssues.some(i => i.type === 'sma_200d_static');
      } else if (column.key === 'sma_200w') {
        hasBiasIssue = biasIssues.some(i => i.type === 'sma_200w_static');
      }
    }

    const value = record[column.key];
    const isNull = value == null;

    // Determine cell background class
    let cellClass = 'py-3 px-4';
    if (column.hasBiasCheck && hasBiasIssue) {
      cellClass += ' relative bg-red-900/30 border-l-2 border-red-500';
    } else if (focusColumns[column.key]) {
      cellClass += ' bg-indigo-700/40';
    } else if (!isNonTrading && isNull) {
      cellClass += ' bg-red-900/20';
    } else {
      cellClass += ' text-white';
    }

    // Format the value
    let displayValue;
    if (isNonTrading) {
      displayValue = '-';
    } else if (column.stocksOnly && isCrypto) {
      displayValue = <span className="text-dark-500">N/A</span>;
    } else if (isNull) {
      displayValue = <span className="text-red-400 font-semibold">NULL</span>;
    } else if (column.isBoolean) {
      // Boolean columns: show Yes/No with color coding
      const boolVal = value === true || value === 1 || value === '1';
      displayValue = boolVal ?
        <span className="text-yellow-400">Yes</span> :
        <span className="text-green-400">No</span>;
    } else if (column.isText) {
      // Text columns: show as-is (truncate if too long)
      const textVal = String(value);
      displayValue = textVal.length > 20 ?
        <span title={textVal}>{textVal.substring(0, 18)}...</span> :
        textVal;
    } else {
      if (column.decimals === 0) {
        displayValue = Math.round(value);
      } else {
        displayValue = value.toFixed(column.decimals);
      }
    }

    return (
      <td key={column.key} className={cellClass}>
        {hasBiasIssue && (
          <div className="absolute -left-1 top-1/2 -translate-y-1/2">
            <AlertTriangle className="w-3 h-3 text-red-400" />
          </div>
        )}
        {displayValue}
      </td>
    );
  };

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-400 mb-2">Error loading data</p>
            <p className="text-dark-400 text-sm mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                fetchAvailableSymbols();
                if (filters.symbol) fetchData();
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-8 h-8 text-orange-400" />
                <h1 className="text-3xl font-bold text-white">Indexes Quality Monitor</h1>
              </div>
              <p className="text-dark-300">
                Monitor individual records and identify missing columns for quality issues
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>


        {/* Filter Controls */}
        <div className="dashboard-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-primary-400" />
              <h3 className="text-lg font-semibold text-white">Filters</h3>
            </div>
          </div>

          {/* Main Filters Row - Single Line */}
          <div className="flex items-end gap-3 mb-4">
            {/* Market Selection */}
            <div className="w-48">
              <label className="block text-xs font-medium text-dark-400 mb-1">Market</label>
              <select
                value={filters.market}
                onChange={(e) => handleFilterChange('market', e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {marketOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Symbol Selection */}
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-dark-400 mb-1">Symbol</label>
              <select
                value={filters.symbol}
                onChange={(e) => handleFilterChange('symbol', e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={!availableSymbols.length}
              >
                <option value="">Select Symbol</option>
                {availableSymbols.map(symbol => (
                  <option key={symbol.symbol_code} value={symbol.symbol_code}>
                    {symbol.symbol_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Data Filter */}
            <div className="w-64">
              <label className="block text-xs font-medium text-dark-400 mb-1">Show</label>
              <select
                value={filters.errors}
                onChange={(e) => handleFilterChange('errors', e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {errorFilterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Quick Period Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleFilterChange('days', 90)}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                  filters.dateRangeType === 'days' && filters.days === 90
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                    : 'bg-dark-700 text-dark-200 hover:bg-dark-600 border border-dark-600'
                }`}
              >
                3M
              </button>
              <button
                onClick={() => handleFilterChange('days', 180)}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                  filters.dateRangeType === 'days' && filters.days === 180
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                    : 'bg-dark-700 text-dark-200 hover:bg-dark-600 border border-dark-600'
                }`}
              >
                6M
              </button>
              <button
                onClick={() => handleFilterChange('days', 365)}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                  filters.dateRangeType === 'days' && filters.days === 365
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                    : 'bg-dark-700 text-dark-200 hover:bg-dark-600 border border-dark-600'
                }`}
              >
                1Y
              </button>
              <button
                onClick={() => handleFilterChange('days', 99999)}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                  filters.dateRangeType === 'days' && filters.days === 99999
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                    : 'bg-dark-700 text-dark-200 hover:bg-dark-600 border border-dark-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('dateRangeType', 'custom')}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                  filters.dateRangeType === 'custom'
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                    : 'bg-dark-700 text-dark-200 hover:bg-dark-600 border border-dark-600'
                }`}
              >
                Custom
              </button>
            </div>

            {/* Custom Date Range Inputs - Only show when Custom is selected */}
            {filters.dateRangeType === 'custom' && (
              <>
                <div className="w-44">
                  <label className="block text-xs font-medium text-dark-400 mb-1">From</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="w-44">
                  <label className="block text-xs font-medium text-dark-400 mb-1">To</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>

          {/* TA Indicators Filter */}
          <div className="border-t border-dark-700 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-dark-400">Visible Columns</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setVisibleColumns({
                    close: true, low: true, drsi: true, wrsi: true, sma_50d: true,
                    dema7: true, dema10: true, dema20: true,
                    weekly_macd_hist: true, daily_macd_hist: true, wsma20: true, wema21: true,
                    sma_200d: true, sma_200w: true, weekly_bb_upper: true, weekly_bb_mid: true,
                    weekly_bb_lower: true, daily_bb_upper: true, daily_bb_mid: true, daily_bb_lower: true,
                    weekly_volatility: true, smart_index_st: true
                  })}
                  className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Select All
                </button>
                <span className="text-dark-600">|</span>
                <button
                  onClick={() => setVisibleColumns({
                    close: false, low: false, drsi: false, wrsi: false, sma_50d: false,
                    dema7: false, dema10: false, dema20: false,
                    weekly_macd_hist: false, daily_macd_hist: false, wsma20: false, wema21: false,
                    sma_200d: false, sma_200w: false, weekly_bb_upper: false, weekly_bb_mid: false,
                    weekly_bb_lower: false, daily_bb_upper: false, daily_bb_mid: false, daily_bb_lower: false,
                    weekly_volatility: false, smart_index_st: false
                  })}
                  className="text-xs text-dark-400 hover:text-dark-300 transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2">
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.close}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, close: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">Close</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.high}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, high: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">High</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.low}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, low: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">Low</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_psar}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_psar: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">PSAR Daily</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_psar_tsl_level}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_psar_tsl_level: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">PSAR TSL</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_psar_deviation}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_psar_deviation: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">PSAR Dev %</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.drsi}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, drsi: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">DRSI</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.wrsi}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, wrsi: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">WRSI</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.sma_50d}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, sma_50d: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">SMA 50D</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.dema7}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, dema7: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">DEMA 7</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.dema10}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, dema10: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">DEMA 10</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.dema20}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, dema20: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">DEMA 20</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.weekly_macd_hist}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, weekly_macd_hist: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">W MACD</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_macd_hist}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_macd_hist: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">D MACD</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.wsma20}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, wsma20: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">WSMA20</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.wema21}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, wema21: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">WEMA21</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.sma_200d}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, sma_200d: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">SMA 200D</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.sma_200w}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, sma_200w: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">SMA 200W</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.weekly_bb_upper}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, weekly_bb_upper: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Upper (W)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.weekly_bb_mid}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, weekly_bb_mid: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Mid (W)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.weekly_bb_lower}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, weekly_bb_lower: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Lower (W)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_bb_upper}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_bb_upper: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Upper (D)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_bb_mid}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_bb_mid: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Mid (D)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.daily_bb_lower}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, daily_bb_lower: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">BB Lower (D)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.weekly_volatility}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, weekly_volatility: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">Volatility</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                <input
                  type="checkbox"
                  checked={visibleColumns.smart_index_st}
                  onChange={(e) => setVisibleColumns(prev => ({ ...prev, smart_index_st: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-dark-200">Smart Index</span>
              </label>
            </div>
          </div>

          {/* Focus Columns Filter */}
          <div className="border-t border-dark-700 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-dark-400">Focus Columns (Highlighted)</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setFocusColumns(Object.keys(focusColumns).reduce((acc, key) => ({ ...acc, [key]: false }), {}))}
                  className="text-xs text-dark-400 hover:text-dark-300 transition-colors"
                >
                  Clear Focus
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2">
              {visibleColumns.close && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.close}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, close: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">Close</span>
                </label>
              )}
              {visibleColumns.high && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.high}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, high: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">High</span>
                </label>
              )}
              {visibleColumns.low && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.low}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, low: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">Low</span>
                </label>
              )}
              {visibleColumns.daily_psar && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_psar}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_psar: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">PSAR Daily</span>
                </label>
              )}
              {visibleColumns.daily_psar_tsl_level && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_psar_tsl_level}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_psar_tsl_level: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">PSAR TSL</span>
                </label>
              )}
              {visibleColumns.daily_psar_deviation && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_psar_deviation}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_psar_deviation: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">PSAR Dev %</span>
                </label>
              )}
              {visibleColumns.drsi && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.drsi}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, drsi: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">DRSI</span>
                </label>
              )}
              {visibleColumns.wrsi && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.wrsi}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, wrsi: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">WRSI</span>
                </label>
              )}
              {visibleColumns.sma_50d && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.sma_50d}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, sma_50d: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">SMA 50D</span>
                </label>
              )}
              {visibleColumns.dema7 && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.dema7}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, dema7: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">DEMA 7</span>
                </label>
              )}
              {visibleColumns.dema10 && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.dema10}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, dema10: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">DEMA 10</span>
                </label>
              )}
              {visibleColumns.dema20 && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.dema20}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, dema20: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">DEMA 20</span>
                </label>
              )}
              {visibleColumns.weekly_macd_hist && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.weekly_macd_hist}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, weekly_macd_hist: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">W MACD</span>
                </label>
              )}
              {visibleColumns.daily_macd_hist && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_macd_hist}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_macd_hist: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">D MACD</span>
                </label>
              )}
              {visibleColumns.wsma20 && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.wsma20}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, wsma20: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">WSMA20</span>
                </label>
              )}
              {visibleColumns.wema21 && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.wema21}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, wema21: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">WEMA21</span>
                </label>
              )}
              {visibleColumns.sma_200d && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.sma_200d}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, sma_200d: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">SMA 200D</span>
                </label>
              )}
              {visibleColumns.sma_200w && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.sma_200w}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, sma_200w: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">SMA 200W</span>
                </label>
              )}
              {visibleColumns.weekly_bb_upper && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.weekly_bb_upper}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, weekly_bb_upper: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Upper (W)</span>
                </label>
              )}
              {visibleColumns.weekly_bb_mid && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.weekly_bb_mid}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, weekly_bb_mid: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Mid (W)</span>
                </label>
              )}
              {visibleColumns.weekly_bb_lower && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.weekly_bb_lower}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, weekly_bb_lower: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Lower (W)</span>
                </label>
              )}
              {visibleColumns.daily_bb_upper && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_bb_upper}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_bb_upper: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Upper (D)</span>
                </label>
              )}
              {visibleColumns.daily_bb_mid && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_bb_mid}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_bb_mid: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Mid (D)</span>
                </label>
              )}
              {visibleColumns.daily_bb_lower && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.daily_bb_lower}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, daily_bb_lower: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">BB Lower (D)</span>
                </label>
              )}
              {visibleColumns.weekly_volatility && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.weekly_volatility}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, weekly_volatility: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">Volatility</span>
                </label>
              )}
              {visibleColumns.smart_index_st && (
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-dark-700/50 rounded px-2 py-1 transition-colors">
                  <input
                    type="checkbox"
                    checked={focusColumns.smart_index_st}
                    onChange={(e) => setFocusColumns(prev => ({ ...prev, smart_index_st: e.target.checked }))}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-dark-200">Smart Index</span>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 border-b border-dark-700">
          <button
            onClick={() => setActiveTab('quality_monitor')}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'quality_monitor'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Quality Monitor</span>
          </button>
          <button
            onClick={() => setActiveTab('non_negotiables')}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'non_negotiables'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Non-Negotiables</span>
          </button>
        </div>

        {/* Quality Monitor Tab */}
        {activeTab === 'quality_monitor' && (
          <>
        {/* Results */}
        {!filters.symbol ? (
              <div className="dashboard-card">
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-dark-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Select a Symbol</h3>
                  <p className="text-dark-400">Choose a market and symbol to view detailed quality records</p>
                </div>
              </div>
            ) : loading ? (
          <div className="dashboard-card">
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-primary-400 mx-auto mb-4 animate-spin" />
              <p className="text-white">Loading records...</p>
            </div>
          </div>
        ) : data && data.records ? (
          <>
            {/* Bias Analysis */}
            {data.bias_analysis && data.bias_analysis.total_issues > 0 && (
              <div className="dashboard-card border-2 border-red-500">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setBiasAnalysisExpanded(!biasAnalysisExpanded)}
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    <h3 className="text-lg font-semibold text-white">
                      Forward-Looking Bias Analysis
                    </h3>
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20">
                      {data.bias_analysis.critical_issues} Critical Issues
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-dark-300">
                      {data.bias_analysis.weeks_analyzed} weeks analyzed
                    </span>
                    {biasAnalysisExpanded ? (
                      <ChevronUp className="w-5 h-5 text-dark-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-dark-400" />
                    )}
                  </div>
                </div>

                {biasAnalysisExpanded && (
                  <>
                    <div className="grid grid-cols-3 gap-4 my-4">
                      <div className="bg-dark-800 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <span className="text-xs text-dark-400">Critical</span>
                        </div>
                        <p className="text-2xl font-bold text-red-400">{data.bias_analysis.critical_issues}</p>
                      </div>
                      <div className="bg-dark-800 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-yellow-400" />
                          <span className="text-xs text-dark-400">Warnings</span>
                        </div>
                        <p className="text-2xl font-bold text-yellow-400">{data.bias_analysis.warning_issues}</p>
                      </div>
                      <div className="bg-dark-800 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-primary-400" />
                          <span className="text-xs text-dark-400">Total Issues</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{data.bias_analysis.total_issues}</p>
                      </div>
                    </div>

                    {/* Issues List */}
                    <div className="space-y-2">
                      {data.bias_analysis.issues.map((issue, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-lg border bg-red-900/10 border-red-800/50"
                        >
                          <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 mt-0.5 text-red-400" />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-sm font-semibold text-red-400">
                                  {issue.week}
                                </span>
                                <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-300">
                                  {issue.type.replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-dark-200">{issue.message}</p>
                              {issue.affected_dates && issue.affected_dates.length > 0 && (
                                <p className="text-xs text-dark-400 mt-1">
                                  Affected dates: {issue.affected_dates.map(d => new Date(d).toLocaleDateString()).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {data.bias_analysis && data.bias_analysis.total_issues === 0 && (
              <div className="dashboard-card border border-green-500/30">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">No Forward-Looking Bias Detected</h3>
                    <p className="text-sm text-dark-300">
                      All weekly indicators show proper progression across {data.bias_analysis.weeks_analyzed} weeks analyzed
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {data.symbol_name} ({data.symbol_code}) - {data.analysis_period}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-dark-300">
                  <span>{data.total_records} records found</span>
                  <span>{formatDate(data.start_date)} to {formatDate(data.end_date)}</span>
                </div>
              </div>
              
              {data.required_columns && (
                <div className="mb-4">
                  <p className="text-sm text-dark-300 mb-2">Required columns being monitored:</p>
                  <div className="flex flex-wrap gap-2">
                    {data.required_columns.map(column => (
                      <span key={column} className="px-2 py-1 bg-dark-700 text-dark-200 text-xs rounded">
                        {column}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Records Table */}
            <div className="bg-dark-900 rounded-xl border border-dark-700 flex flex-col sticky top-0" style={{ maxHeight: '100vh' }}>
              {/* Header Section */}
              <div className="flex-shrink-0 bg-dark-900 rounded-t-xl">
                <div className="flex items-center justify-between p-6 border-b border-dark-700">
                  <h3 className="text-lg font-semibold text-white">Data Quality Records</h3>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={exportToCSV}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm font-medium">Export CSV</span>
                    </button>
                    <button
                      onClick={fetchData}
                      className="flex items-center space-x-2 px-3 py-1 text-dark-300 hover:text-white transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-sm">Refresh</span>
                    </button>
                  </div>
                </div>

                {/* Bias Visual Legend */}
                {data.bias_analysis && data.bias_analysis.total_issues > 0 && (
                  <div className="mx-6 mt-4 mb-4 p-3 bg-dark-800 rounded-lg border border-dark-600">
                    <div className="flex items-center space-x-4 text-xs flex-wrap gap-2">
                      <span className="text-dark-400 font-medium">Forward-Looking Bias Indicators:</span>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 bg-red-900/30 border-l-2 border-red-500 rounded-sm"></div>
                          <AlertTriangle className="w-3 h-3 text-red-400" />
                        </div>
                        <span className="text-dark-200">Static values detected (no change during week)</span>
                      </div>
                      <span className="text-dark-400">|</span>
                      <span className="text-dark-300">Checks: Weekly MACD, SMA 50D, SMA 200D, SMA 200W, BB Lower (Weekly)</span>
                    </div>
                  </div>
                )}
              </div>

              {!data.records || data.records.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    {filters.errors === 'errors_only' ? 'No Issues Found! 🎉' : 'No Records Found'}
                  </h3>
                  <p className="text-dark-400">
                    {filters.errors === 'errors_only'
                      ? `All records for ${data.symbol_name} are healthy for the selected ${filters.days}-day period. Data quality looks great!`
                      : 'No records found for the selected criteria'}
                  </p>
                </div>
              ) : (
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-primary-500">
                        <th className="sticky top-0 left-0 z-[60] text-left py-4 px-4 text-dark-100 font-semibold bg-dark-700 border-r-2 border-primary-600 shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
                          Date
                        </th>
                        {visibleColumns.smart_index_st && (
                          <th className={`sticky top-0 z-50 text-left py-4 px-4 text-dark-100 font-semibold shadow-[0_2px_4px_rgba(0,0,0,0.4)] ${focusColumns.smart_index_st ? 'bg-indigo-700/60' : 'bg-dark-700'}`}>
                            Smart Index
                          </th>
                        )}
                        {sortedColumns.map(column => renderColumnHeader(column))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.records.map((record, index) => {
                        const isHoliday = record.health_status === 'holiday';
                        const isWeekend = record.health_status === 'weekend';
                        const isNonTrading = isHoliday || isWeekend;
                        const isMissing = record.health_status === 'missing';
                        const isCrypto = filters.market === 'crypto';
                        const isStocks = filters.market === 'india' || filters.market === 'us';

                        // Check if this date has any bias issues
                        const biasIssues = data.bias_analysis?.issues.filter(issue =>
                          issue.affected_dates?.includes(record.date)
                        ) || [];
                        const hasCriticalBias = biasIssues.some(i => i.severity === 'critical');
                        const hasWarningBias = biasIssues.some(i => i.severity === 'warning');

                        // Get specific column issues
                        const hasMacdIssue = biasIssues.some(i => i.type === 'weekly_macd_static');
                        // WRSI check removed - it's based on Friday close, so same value Mon-Thu is EXPECTED
                        const hasWrsiIssue = false;
                        const hasBbIssue = biasIssues.some(i => i.type === 'bb_static');
                        const hasSma50dIssue = biasIssues.some(i => i.type === 'sma_50d_static');
                        const hasSma200dIssue = biasIssues.some(i => i.type === 'sma_200d_static');
                        const hasSma200wIssue = biasIssues.some(i => i.type === 'sma_200w_static');

                        // Determine row background color based on health status
                        const rowBgColor =
                          record.health_status === 'critical' || record.health_status === 'missing' ? 'bg-red-900/20 border-l-4 border-red-500' :
                          record.health_status === 'warning' ? 'bg-yellow-900/15 border-l-4 border-yellow-500' :
                          record.health_status === 'holiday' ? 'bg-blue-900/10' :
                          record.health_status === 'weekend' ? 'bg-gray-900/10' :
                          hasCriticalBias ? 'bg-red-900/10 border-l-4 border-red-400' :
                          hasWarningBias ? 'bg-yellow-900/5' : '';

                        return (
                          <tr key={record.id} className={`border-b border-dark-800 hover:bg-dark-800/50 ${rowBgColor}`}>
                            <td className={`py-3 px-4 text-white font-medium sticky left-0 z-10 border-r-2 border-primary-600/50 ${
                              record.health_status === 'critical' || record.health_status === 'missing' ? 'bg-red-900/40' :
                              record.health_status === 'warning' ? 'bg-yellow-900/30' :
                              record.health_status === 'holiday' ? 'bg-blue-900/30' :
                              record.health_status === 'weekend' ? 'bg-gray-800/80' :
                              hasCriticalBias ? 'bg-red-900/20' :
                              hasWarningBias ? 'bg-yellow-900/10' : 'bg-dark-900'
                            }`}>
                              {formatDate(record.date)}
                            </td>
                            {visibleColumns.smart_index_st && (
                              <td className={`py-3 px-4 ${focusColumns.smart_index_st ? 'bg-indigo-700/40' : ''}`}>
                                {isWeekend ? (
                                  <span className="text-gray-400 text-xs">-</span>
                                ) : isHoliday ? (
                                  <span className="text-blue-400 text-xs">-</span>
                                ) : record.smart_index_st !== null ? (
                                  <span className={`font-semibold ${
                                    record.smart_index_st >= 1 && record.smart_index_st <= 30
                                      ? 'text-red-400'
                                      : record.smart_index_st >= 31 && record.smart_index_st <= 70
                                      ? 'text-gray-300'
                                      : 'text-green-400'
                                  }`}>
                                    {record.smart_index_st}
                                  </span>
                                ) : (
                                  <span className="text-red-400 font-semibold">NULL</span>
                                )}
                              </td>
                            )}
                            {sortedColumns.map(column => renderColumnCell(column, record, biasIssues, isNonTrading, isCrypto, isStocks))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="dashboard-card">
            <div className="text-center py-8">
              <Database className="w-12 h-12 text-dark-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Data Available</h3>
              <p className="text-dark-400">No records found for the selected symbol and time period</p>
            </div>
          </div>
        )}
          </>
        )}

        {/* Non-Negotiables Tab */}
        {activeTab === 'non_negotiables' && (
          <div className="space-y-4">
            {/* Inject validation report styles */}
            <style>{validationReportStyles}</style>

            {!filters.symbol ? (
              <div className="dashboard-card">
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-dark-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Select a Symbol</h3>
                  <p className="text-dark-400">Choose a market and symbol to view the validation report</p>
                </div>
              </div>
            ) : nonNegotiablesLoading ? (
              <div className="dashboard-card">
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-primary-400 mx-auto mb-4 animate-spin" />
                  <p className="text-white">Loading validation report...</p>
                </div>
              </div>
            ) : nonNegotiablesError ? (
              <div className="dashboard-card">
                <div className="text-center py-8">
                  <ShieldCheck className="w-12 h-12 text-dark-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Validation Report</h3>
                  <p className="text-dark-400">{nonNegotiablesError}</p>
                </div>
              </div>
            ) : nonNegotiablesHtml ? (
              <div className="bg-dark-800/30 rounded-lg overflow-hidden">
                <div className="bg-dark-700 px-4 py-3 border-b border-dark-600 flex items-center justify-between">
                  <h4 className="text-white font-medium flex items-center">
                    <ShieldCheck className="w-4 h-4 mr-2 text-primary-400" />
                    {filters.symbol.toUpperCase()} - Non-Negotiables Validation
                  </h4>
                  <button
                    onClick={fetchNonNegotiables}
                    disabled={nonNegotiablesLoading}
                    className="text-xs px-3 py-1 bg-green-600/20 text-green-400 rounded hover:bg-green-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <RefreshCw className={`w-3 h-3 ${nonNegotiablesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
                <div className="p-2">
                  <div
                    className="validation-report-container rounded-lg overflow-auto"
                    style={{ maxHeight: '700px' }}
                    dangerouslySetInnerHTML={{ __html: nonNegotiablesHtml }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Layout>
  );
};

// CSS for validation report container
const validationReportStyles = `
  .validation-report-container {
    background: #1e1e1e;
    color: #e0e0e0;
    font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
    padding: 20px;
    line-height: 1.5;
  }
  .validation-report-container h1 {
    color: #dcdcaa;
    border-bottom: 2px solid #404040;
    padding-bottom: 10px;
    margin-bottom: 20px;
    font-size: 1.5em;
  }
  .validation-report-container .section-title {
    color: #569cd6;
    font-size: 1.1em;
    margin: 25px 0 10px 0;
    padding: 5px 0;
    border-bottom: 1px dashed #404040;
  }
  .validation-report-container table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
  }
  .validation-report-container th,
  .validation-report-container td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #404040;
  }
  .validation-report-container th {
    color: #888;
    font-weight: normal;
    text-transform: uppercase;
    font-size: 0.85em;
  }
  .validation-report-container .pass { color: #4ec9b0; }
  .validation-report-container .fail { color: #f14c4c; }
  .validation-report-container .icon { font-size: 1.2em; }
  .validation-report-container .note { color: #888; font-size: 0.9em; }
  .validation-report-container .signal-item {
    margin: 10px 0;
    padding: 12px;
    background: rgba(0,0,0,0.3);
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .validation-report-container .signal-id { color: #dcdcaa; font-weight: bold; }
  .validation-report-container .signal-desc { flex: 1; }
  .validation-report-container .summary-box {
    margin-top: 25px;
    padding: 15px;
    border-radius: 8px;
    text-align: center;
    font-size: 1.2em;
  }
  .validation-report-container .summary-pass {
    background: rgba(78, 201, 176, 0.2);
    border: 2px solid #4ec9b0;
    color: #4ec9b0;
  }
  .validation-report-container .summary-fail {
    background: rgba(241, 76, 76, 0.2);
    border: 2px solid #f14c4c;
    color: #f14c4c;
  }
  .validation-report-container .timestamp {
    color: #888;
    font-size: 0.85em;
    text-align: right;
    margin-top: 20px;
  }
`;

export default Indexes;
